import { NotificationTypes } from '@accounts/accounts-alerts/accounts-alerts.interface';
import { ICellRendererParams } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { AnalyticsService } from '@frk/eds-components';
import { AgGridHelperService } from '@products/fund-listing/services/ag-grid-helper.service';
import { ProductFilterStateManagerService } from '@products/fund-listing/services/product-filter-state-manager.service';
import { EMDASH } from '@products/utils/constants/product.constants';
import { FundNameData, ProductData } from '@types';
import { Logger } from '@utils/logger';
import get from 'lodash/get';

const logger = Logger.getLogger('FundNameCellRendererComponent');

@Component({
  selector: 'ft-fund-name-cell-renderer',
  templateUrl: './fund-name-cell-renderer.component.html',
  styleUrls: ['./fund-name-cell-renderer.component.scss'],
})
export class FundNameCellRendererComponent {
  public fundDetail: FundNameData;
  public fundLink: string;
  public showSalesCharges: boolean;
  public showPerformanceAndMaintananceCharges: boolean;
  public maxIntialSalesCharge: string;
  public performanceCharge: string;
  public maintananceCharge: string;
  public cdsc: string;
  public isSoftLaunch: boolean;
  public ppssMessage: string;
  public ppssTooltip: string;
  public notificationType: NotificationTypes;
  public showTooltipAtTop: boolean;
  public showLegendIcons = false;
  public product: ProductData;
  public params: any;

  constructor(
    private agGridHelper: AgGridHelperService,
    private analyticsService: AnalyticsService,
    private filterStateManagerService: ProductFilterStateManagerService
  ) {}

  public agInit(params: ICellRendererParams): void {
    this.params = params.data;
    const showSecondSuffix = params.column.getColDef().cellRendererParams
      ?.showSecondSuffix;
    this.product = params.data;
    this.showSalesCharges = get(params, 'showSalesCharges', true);
    this.showPerformanceAndMaintananceCharges = get(
      params,
      'showPerformanceAndMaintananceCharges',
      false
    );
    this.showLegendIcons = get(params, 'showLegendIcons', false);
    this.fundDetail = this.agGridHelper.getFundDetailToDisplay(
      this.product,
      showSecondSuffix
    );
    // NGC-10974: To make tooltip appear at top for the last two elements.
    // update 6/1/22. If currentPage and pageSize don't exist on component, get last index from api
    const currentPage: number = params.context?.currentPage;
    const pageSize: number = params.context?.pageSize;
    const lastRowIndex =
      currentPage && pageSize
        ? currentPage * pageSize - 1
        : params.api.getDisplayedRowCount() - 1;

    if (
      lastRowIndex === params.rowIndex ||
      lastRowIndex - 1 === params.rowIndex ||
      params.context?.filteredRows.length === params.rowIndex ||
      params.context?.filteredRows.length - 1 === params.rowIndex
    ) {
      this.showTooltipAtTop = true;
    } else {
      this.showTooltipAtTop = false;
    }
    const salesCharge = this.product.shareClass?.charges?.maxIntialSalesCharge;
    const cdscValue = this.product.shareClass?.charges?.cdsc;

    this.maintananceCharge = this.product.shareClass?.charges?.maintananceCharge;
    this.performanceCharge = this.product.shareClass?.charges?.performanceFeeCharge;

    if (salesCharge !== EMDASH && cdscValue !== EMDASH) {
      this.maxIntialSalesCharge = salesCharge;
      this.cdsc = cdscValue;
    } else {
      this.showSalesCharges = false;
    }

    if (
      this.maintananceCharge === EMDASH &&
      this.performanceCharge === EMDASH
    ) {
      this.showPerformanceAndMaintananceCharges = false;
    }
    this.isSoftLaunch = this.product.shareClass?.isSoftLaunch;
    this.fundLink = this.product.shareClass?.link;
    this.notificationType = NotificationTypes.ALERT;
    const checkPpssMessage = this.product.ppssAlert?.ppssMessage;
    const checkPpssTooltip = this.product.ppssAlert?.ppssTooltip;
    if (checkPpssMessage) {
      this.ppssMessage = checkPpssMessage;
    }
    if (checkPpssTooltip) {
      this.ppssTooltip = checkPpssTooltip;
    }
  }

  public onLinkClick(event: Event): void {
    if (this.params?.fundName) {
      this.trackSelectorItem(this.params?.fundName, this.fundLink);
    }
  }

  private trackSelectorItem(fundName: string, fundLink: string): void {
    this.analyticsService.trackEvent({
      event: 'select_content',
      detailed_event: 'Select Content',
      event_data: {
        content_type: 'Product',
        index: '',
        link_text: fundName,
        link_url: fundLink,
        search_category: 'PPPS',
        ...this.filterStateManagerService.getAppliedFilterData(),
      },
    });
  }
}
